/**
 * Local SQLite connection editor — the right-sidebar form rendered when
 * the user picks the "Local SQLite" tile in NewConnectionPanel. Drives
 * a 5-state machine: idle → attaching → (attached | duplicate | error).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "components/common/Button";
import { InlineError } from "components/common/InlineError";
import {
  attachLocalSqlite,
  type AttachFlowResult,
} from "components/canvas/leftSide/import/LocalSqlite";
import {
  detectFileTypeFromFile,
  FileType,
} from "components/canvas/fileUpload/useWorkerOperation";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useMintOrgId } from "components/main/connections/useMintOrg";
import { useConnectionsStore } from "stores/connectionsStore";
import { formatBytes } from "components/canvas/detailsSide/utils/formatBytes";
import {
  attachSqliteToOpfsViaWorker,
  pickSqliteFile,
} from "components/main/utils/sqlite/sqliteAttachShared";
import {
  createLocalSqliteSavedConnection,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";

interface Props {
  onSaved: (saved: SavedConnection) => void;
  onCancel: () => void;
  /** NewConnectionPanel owns the header back-arrow + close button, so
   *  the in-form Cancel button is hidden in that surface. Stand-alone
   *  callers (none today) can render it. */
  hideCancel?: boolean;
}

type ProgressStep = "verified" | "copied" | "registering" | "saving";

type EditorState =
  | { kind: "idle"; dragOver: boolean }
  | {
      kind: "attaching";
      filename: string;
      fileSize: number;
      step: ProgressStep;
    }
  | { kind: "error"; message: string; hint?: string }
  | {
      kind: "duplicate";
      filename: string;
      existingConnectionId: string;
      existingDisplayName: string;
    }
  | {
      /** Attach completed. The panel stays open with an in-panel
       *  success banner + Close button so the user gets to read what
       *  happened instead of having the side sheet snap shut on them.
       *  The `savedConnection` is held until the user clicks Close,
       *  at which point onSaved fires and the parent panel closes. */
      kind: "attached";
      filename: string;
      fileSize: number;
      savedConnection: SavedConnection;
    };

/** Strip the extension so the connections list reads "northwind" rather
 *  than "northwind.sqlite". */
function defaultDisplayName(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
}

export const LocalSqliteEditor: React.FC<Props> = ({
  onSaved,
  onCancel,
  hideCancel = false,
}) => {
  const mintOrgId = useMintOrgId();
  const worker = useBoundStore((s) => s.worker);
  const initializeWorker = useBoundStore((s) => s.initializeWorker);
  // The worker is normally booted by CanvasApp on model open. When the
  // user lands on /connections directly (no canvas mounted yet), the
  // worker is null and the attach round-trip would throw with the
  // "Worker not ready" message. Init is idempotent — safe to call from
  // here even when CanvasApp has already booted it elsewhere.
  useEffect(() => {
    if (!worker) initializeWorker();
  }, [worker, initializeWorker]);
  const refetch = useConnectionsStore((s) => s.refetch);
  const [state, setState] = useState<EditorState>({
    kind: "idle",
    dragOver: false,
  });
  // Guard against concurrent attaches when the user clicks Select while
  // a previous attach hasn't returned. We don't render the button in
  // that state anyway, but ref keeps the guard robust across async
  // boundaries.
  const inFlight = useRef(false);

  const stepRef = useRef<ProgressStep>("verified");

  const advanceStep = useCallback((step: ProgressStep, fileSize?: number) => {
    stepRef.current = step;
    setState((prev) => {
      if (prev.kind !== "attaching") return prev;
      return {
        ...prev,
        step,
        ...(fileSize !== undefined ? { fileSize } : {}),
      };
    });
  }, []);

  const runAttach = useCallback(
    async (handle: FileSystemFileHandle, file: File) => {
      if (inFlight.current) return;
      inFlight.current = true;
      stepRef.current = "verified";
      setState({
        kind: "attaching",
        filename: file.name,
        fileSize: file.size,
        step: "verified",
      });

      let result: AttachFlowResult;
      try {
        result = await attachLocalSqlite(
          { displayName: defaultDisplayName(file.name), handle },
          {
            attachToOpfs: async (_f, h) => {
              const r = await attachSqliteToOpfsViaWorker(worker, h);
              advanceStep("copied", r.fileSize);
              return r;
            },
            createConnection: async (body) => {
              advanceStep("registering");
              const saved = await createLocalSqliteSavedConnection({
                ...body,
                org_id: mintOrgId,
              });
              advanceStep("saving");
              return saved;
            },
          },
        );
      } catch (e) {
        inFlight.current = false;
        setState({
          kind: "error",
          message: e instanceof Error ? e.message : "Attach failed.",
        });
        return;
      }

      inFlight.current = false;

      switch (result.kind) {
        case "attached": {
          // Refetch so the new row is visible to the connections list
          // the moment the user clicks Close. Non-fatal if it fails —
          // the connection is already saved and the list will refresh
          // on the next user action.
          try {
            await refetch();
          } catch {
            // intentional swallow — see above
          }
          // No global toast: the in-panel success banner (see
          // AttachedView below) gives the user the same confirmation
          // without the noise of a top-of-screen overlay. The panel
          // stays open with a Close button.
          setState({
            kind: "attached",
            filename: file.name,
            fileSize: result.record.fileSize,
            savedConnection: result.savedConnection,
          });
          return;
        }
        case "duplicate":
          setState({
            kind: "duplicate",
            filename: file.name,
            existingConnectionId: result.existingConnectionId,
            existingDisplayName: result.existingDisplayName,
          });
          return;
        case "blocked-too-large":
          setState({
            kind: "error",
            message: "Not enough browser storage.",
            hint: `Need ${formatBytes(result.fileSize)}, only ${formatBytes(result.remainingBytes)} free.`,
          });
          return;
        case "unsupported-browser":
          setState({
            kind: "error",
            message: result.reason,
            hint: "Open in Chrome, Edge, or Arc.",
          });
          return;
        case "user-cancelled":
          setState({ kind: "idle", dragOver: false });
          return;
      }
    },
    [advanceStep, worker, refetch],
  );

  /** Magic-header sniff + sniff-vs-extension classification before the
   *  worker round-trip. Surfaces the rename hint for `.csv`-with-SQLite-
   *  magic and "not a SQLite file" for `.db`-without-magic. */
  const verifyAndAttach = useCallback(
    async (handle: FileSystemFileHandle, file: File) => {
      const detection = await detectFileTypeFromFile(file);
      if (detection.kind === "csv-with-sqlite-magic") {
        setState({
          kind: "error",
          message: `${detection.filename} looks like a SQLite database, not a CSV.`,
          hint: "Rename it to .sqlite or drag it onto the canvas as a database.",
        });
        return;
      }
      if (
        detection.kind === "sqlite-extension-without-magic" ||
        detection.kind !== "route" ||
        detection.fileType !== FileType.LocalSqlite
      ) {
        const name = "filename" in detection ? detection.filename : file.name;
        setState({
          kind: "error",
          message: `${name} isn't a SQLite database.`,
          hint: "Pick a .sqlite, .db, or .sqlite3 file.",
        });
        return;
      }
      await runAttach(handle, file);
    },
    [runAttach],
  );

  const handlePickFile = useCallback(async () => {
    const picked = await pickSqliteFile();
    if (picked.kind === "unsupported") {
      setState({
        kind: "error",
        message: "This browser can't open files.",
        hint: "Open in Chrome, Edge, or Arc.",
      });
      return;
    }
    if (picked.kind === "cancelled") {
      setState({ kind: "idle", dragOver: false });
      return;
    }
    const file = await picked.handle.getFile();
    await verifyAndAttach(picked.handle, file);
  }, [verifyAndAttach]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prev) =>
      prev.kind === "idle" ? { ...prev, dragOver: true } : prev,
    );
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setState((prev) =>
      prev.kind === "idle" ? { ...prev, dragOver: false } : prev,
    );
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setState((prev) =>
        prev.kind === "idle" ? { ...prev, dragOver: false } : prev,
      );
      const items = e.dataTransfer?.items;
      if (!items || items.length === 0) return;

      // Modern Chromium drag-drop exposes the FSA handle directly.
      // Older browsers will only have `getAsFile` — we still take the
      // File so the user gets a clear "your browser can't" message
      // via the capability gate instead of a silent no-op.
      const item = items[0];
      const itemWithFsa = item as DataTransferItem & {
        getAsFileSystemHandle?: () => Promise<
          FileSystemFileHandle | FileSystemDirectoryHandle | null
        >;
      };
      if (typeof itemWithFsa.getAsFileSystemHandle === "function") {
        const handle = await itemWithFsa.getAsFileSystemHandle();
        if (handle && handle.kind === "file") {
          const fileHandle = handle as FileSystemFileHandle;
          const file = await fileHandle.getFile();
          await verifyAndAttach(fileHandle, file);
          return;
        }
      }
      // Fallback: getAsFileSystemHandle missing or returned a directory.
      // The capability gate should already have blocked this browser,
      // but surface a clean error if we end up here anyway.
      setState({
        kind: "error",
        message: "This browser can't open local files.",
        hint: "Open in Chrome, Edge, or Arc.",
      });
    },
    [verifyAndAttach],
  );

  const reset = useCallback(
    () => setState({ kind: "idle", dragOver: false }),
    [],
  );

  // -- Render --------------------------------------------------------------

  if (state.kind === "attaching") {
    return (
      <AttachingView
        filename={state.filename}
        fileSize={state.fileSize}
        step={state.step}
      />
    );
  }

  if (state.kind === "duplicate") {
    return (
      <DuplicateView
        filename={state.filename}
        existingDisplayName={state.existingDisplayName}
        onOpenExisting={() => {
          // Surface the existing connection in the connections store
          // (refetch in case it was created in another tab) and close
          // the panel so the user sees the list with the existing row.
          refetch().catch(() => undefined);
          showNotificationGlobal(
            `Already attached as "${state.existingDisplayName}".`,
            "default",
          );
          onCancel();
        }}
        onTryAgain={reset}
        hideCancel={hideCancel}
        onCancel={onCancel}
      />
    );
  }

  if (state.kind === "attached") {
    return (
      <AttachedView
        filename={state.filename}
        fileSize={state.fileSize}
        onClose={() => onSaved(state.savedConnection)}
      />
    );
  }

  return (
    <IdleOrErrorView
      state={state}
      onPickFile={handlePickFile}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onTryAgain={reset}
      hideCancel={hideCancel}
      onCancel={onCancel}
    />
  );
};

// ----------------------------------------------------------------------------
// Sub-views
// ----------------------------------------------------------------------------

const IdleOrErrorView: React.FC<{
  state: Extract<EditorState, { kind: "idle" } | { kind: "error" }>;
  onPickFile: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onTryAgain: () => void;
  hideCancel: boolean;
  onCancel: () => void;
}> = ({
  state,
  onPickFile,
  onDragOver,
  onDragLeave,
  onDrop,
  onTryAgain,
  hideCancel,
  onCancel,
}) => {
  const isError = state.kind === "error";
  const dragOver = state.kind === "idle" && state.dragOver;
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-fg-bold mb-1">Pick a SQLite database</p>
        <p className="text-fg-muted-regular-small">
          Bytes stay in your browser. Tables become editable Mycorr models.
          Foreign keys auto-link related tables.
        </p>
      </div>
      <div
        role="region"
        aria-label="SQLite file dropzone"
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className="flex flex-col items-center gap-2 p-6 rounded-app border border-dashed text-center transition-colors"
        style={{
          borderColor: isError
            ? "var(--color-error)"
            : dragOver
              ? "var(--color-accent)"
              : "var(--color-border)",
          background: isError
            ? "var(--color-error-bg)"
            : dragOver
              ? "var(--color-accent-subtle)"
              : "var(--color-bg-subtle)",
        }}
      >
        {isError ? (
          <>
            <span
              className="text-fg-bold"
              style={{ color: "var(--color-error)" }}
            >
              {state.message}
            </span>
            {state.hint && (
              <span className="text-fg-muted-regular-small">{state.hint}</span>
            )}
            <Button
              variant="primary"
              size="md"
              onClick={onTryAgain}
              className="mt-2"
            >
              Try another file
            </Button>
          </>
        ) : (
          <>
            <span className="text-fg-bold">
              Drop a .sqlite, .db, or .sqlite3 file
            </span>
            <span className="text-fg-muted-regular-small">or</span>
            <Button variant="primary" size="md" onClick={onPickFile}>
              Select database file
            </Button>
          </>
        )}
      </div>
      {!hideCancel && (
        <div className="flex justify-end">
          <Button variant="secondary" size="md" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

const STEP_LABELS: { id: ProgressStep; active: string; done: string }[] = [
  {
    id: "verified",
    active: "File verified — SQLite format detected",
    done: "File verified — SQLite format detected",
  },
  {
    id: "copied",
    active: "Copied to browser storage",
    done: "Copied to browser storage",
  },
  {
    id: "registering",
    active: "Registering connection on the server…",
    done: "Registered connection on the server",
  },
  { id: "saving", active: "Saving locally", done: "Saved locally" },
];

const MetadataStrip: React.FC<{ filename: string; fileSize: number }> = ({
  filename,
  fileSize,
}) => (
  <div className="flex justify-between items-center px-3 py-2 rounded-app border border-border bg-bg-subtle">
    <span className="font-mono text-fg-muted-regular-small">{filename}</span>
    <span className="text-fg-muted-regular-small">{formatBytes(fileSize)}</span>
  </div>
);

/** Step list. `activeIndex === STEP_LABELS.length` means all-done. */
const ProgressSteps: React.FC<{ activeIndex: number }> = ({ activeIndex }) => (
  <ol
    className="flex flex-col gap-2 p-3 rounded-app border border-border bg-bg-subtle"
    aria-live="polite"
  >
    {STEP_LABELS.map((s, i) => {
      const done = i < activeIndex;
      const active = i === activeIndex;
      return (
        <li
          key={s.id}
          className="flex items-center gap-2 text-fg-muted-regular-small"
          style={{ color: done || active ? "var(--color-fg)" : undefined }}
        >
          <StepIcon state={done ? "done" : active ? "active" : "pending"} />
          <span>{done ? s.done : s.active}</span>
        </li>
      );
    })}
  </ol>
);

const AttachingView: React.FC<{
  filename: string;
  fileSize: number;
  step: ProgressStep;
}> = ({ filename, fileSize, step }) => (
  <div className="flex flex-col gap-4">
    <p className="text-fg-bold">Attaching {filename}</p>
    <p className="text-fg-muted-regular-small">
      Stays in your browser. Won't be uploaded.
    </p>
    <MetadataStrip filename={filename} fileSize={fileSize} />
    <ProgressSteps activeIndex={STEP_LABELS.findIndex((s) => s.id === step)} />
  </div>
);

const CHECK_PATH = "M20 6L9 17l-5-5";

const StepIcon: React.FC<{ state: "done" | "active" | "pending" }> = ({
  state,
}) => {
  if (state === "done") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        style={{ color: "var(--color-success)" }}
        aria-hidden="true"
      >
        <path d={CHECK_PATH} />
      </svg>
    );
  }
  if (state === "active") {
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        style={{
          color: "var(--color-accent)",
          animation: "gstack-sqlite-spin 1s linear infinite",
        }}
        aria-hidden="true"
      >
        <style>{`@keyframes gstack-sqlite-spin { to { transform: rotate(360deg) } }`}</style>
        <circle cx="12" cy="12" r="9" strokeDasharray="20 40" opacity="0.4" />
        <circle
          cx="12"
          cy="12"
          r="9"
          strokeDasharray="40 20"
          strokeDashoffset="-20"
        />
      </svg>
    );
  }
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
};

const DuplicateView: React.FC<{
  filename: string;
  existingDisplayName: string;
  onOpenExisting: () => void;
  onTryAgain: () => void;
  hideCancel: boolean;
  onCancel: () => void;
}> = ({
  filename,
  existingDisplayName,
  onOpenExisting,
  onTryAgain,
  hideCancel,
  onCancel,
}) => (
  <div className="flex flex-col gap-4">
    <div>
      <p className="text-fg-bold mb-1">Pick a SQLite database</p>
      <p className="text-fg-muted-regular-small">
        Bytes stay in your browser. Tables become editable Mycorr models.
      </p>
    </div>
    <InlineError>
      {filename} is already attached as &ldquo;{existingDisplayName}&rdquo;.
    </InlineError>
    <div className="flex flex-col gap-2">
      <Button variant="primary" size="md" onClick={onOpenExisting}>
        Open existing connection
      </Button>
      <Button variant="secondary" size="md" onClick={onTryAgain}>
        Pick a different file
      </Button>
    </div>
    {!hideCancel && (
      <div className="flex justify-end">
        <Button variant="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    )}
  </div>
);

/** Renders the in-panel success banner + Close button. All steps show
 *  as done; clicking Close fires `onSaved`. */
const AttachedView: React.FC<{
  filename: string;
  fileSize: number;
  onClose: () => void;
}> = ({ filename, fileSize, onClose }) => (
  <div className="flex flex-col gap-4">
    <p className="text-fg-bold">{filename} attached</p>
    <p className="text-fg-muted-regular-small">
      Stayed in your browser. Won't be uploaded.
    </p>
    <MetadataStrip filename={filename} fileSize={fileSize} />
    <ProgressSteps activeIndex={STEP_LABELS.length} />
    <p
      role="status"
      className="flex items-start gap-2 text-fg-muted-regular-small"
      style={{ color: "var(--color-success)" }}
    >
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        style={{ flex: "none", marginTop: 2 }}
        aria-hidden="true"
      >
        <path d={CHECK_PATH} />
      </svg>
      <span>Connection saved. Open the Connections page to import tables.</span>
    </p>
    <div className="flex justify-end">
      <Button variant="secondary" size="md" onClick={onClose}>
        Close
      </Button>
    </div>
  </div>
);
